/**
 * SolutionAnalysis — Shared AI analysis display (SWOT + Lenses + Evaluation + Conclusion).
 * Extracted from ReportSolutions to be reused in both report and live workshop modal.
 * Single source of truth — DO NOT clone.
 */
import React from 'react';
import styled from '@emotion/styled';
import { Target, Lightbulb, TrendingUp, Shield, AlertTriangle, Zap, BookOpen, ExternalLink } from 'lucide-react';
import { sanitizeAiObject } from '../../utils/sanitizeAiText';
import { isValidEvaluationTagId } from '@kuutio/shared/evaluationTags.js';
import { METRIC_STYLES } from './reportStyles';
import EditableText from './EditableText';
import EvaluationTagChips from '../Solutions/EvaluationTagChips';
import SourceTag from '../AIReferences/SourceTag';
import { TIER_ORDER, getTier } from '../AIReferences/tiers';
import { useTranslation } from '../../hooks/useTranslation.jsx';

// ─── SWOT ──────────────────────────────────────────────────────────────────

const AnalysisSection = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 2px solid ${p => p.$borderColor || '#ede9fe'};
`;

const AnalysisHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
`;

const AnalysisTag = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  color: #7c3aed;
  background: #ede9fe;
`;

const AnalysisTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${p => p.$color || '#000'};
  margin: 0;
`;

const AnalysisDivider = styled.div`
  margin: 28px 0;
  border-top: 1px solid ${p => p.$color || '#e5e7eb'};
`;

const SwotGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

// Labels are translation keys: this panel had no i18n at all, so an English
// workshop's report showed English AI analysis under Finnish SWOT headings.
const SWOT_STYLES = {
  strengths: { bg: '#f0fdf4', border: '#bbf7d0', title: '#166534', icon: TrendingUp, labelKey: 'solutionAnalysis.swot.strengths' },
  weaknesses: { bg: 'rgba(232, 17, 75, 0.08)', border: '#fecaca', title: '#a83d10', icon: AlertTriangle, labelKey: 'solutionAnalysis.swot.weaknesses' },
  opportunities: { bg: '#f5f5f5', border: '#bfdbfe', title: '#2c5e91', icon: Lightbulb, labelKey: 'solutionAnalysis.swot.opportunities' },
  threats: { bg: '#fefce8', border: '#fef08a', title: '#854d0e', icon: Shield, labelKey: 'solutionAnalysis.swot.threats' },
};

const SwotCard = styled.div`
  background: ${p => p.$bg};
  border: 1px solid ${p => p.$border};
  border-radius: 2px;
  padding: 14px;
`;

const SwotCardTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${p => p.$color};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SwotList = styled.ul`
  margin: 0;
  padding-left: 16px;
  font-size: 13px;
  color: #4b5563;
  line-height: 1.6;
  li { margin-bottom: 4px; }
`;

// ─── Lens Focus ────────────────────────────────────────────────────────────

const InsightLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.$color || '#6b7280'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LensFocusWrap = styled.div`
  margin-top: 0;
`;

const LensFocusRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

const FOCUS_LEVEL = {
  2: { bg: '#f5f5f5', border: '#93c5fd', color: '#2c5e91' },
  1: { bg: '#f0f9ff', border: '#bae6fd', color: '#0369a1' },
  0: { bg: '#f9fafb', border: '#e5e7eb', color: '#9ca3af' },
};

const LensChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 600;
  background: ${p => p.$bg};
  border: 1px solid ${p => p.$border};
  color: ${p => p.$color};
  opacity: ${p => p.$intensity === 0 ? 0.5 : 1};
`;

const LensSign = styled.span`
  font-size: 10px;
  font-weight: 800;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$positive ? '#dcfce7' : p.$negative ? '#fee2e2' : 'transparent'};
  color: ${p => p.$positive ? '#00613d' : p.$negative ? '#fa6129' : '#9ca3af'};
`;

const LensReasonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const LensReasonItem = styled.div`
  font-size: 11px;
  color: ${p => p.$textColor || '#6b7280'};
  line-height: 1.4;
  padding-left: 8px;
  border-left: 2px solid ${p => p.$intensity >= 2 ? '#93c5fd' : p.$intensity >= 1 ? '#f5f5f5' : '#e5e7eb'};
`;

const LensReasonName = styled.span`
  font-weight: 600;
  color: ${p => p.$color || '#374151'};
`;

// ─── Evaluation ────────────────────────────────────────────────────────────

const EvalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin: 16px 0;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

// Shared with ReportSolutions / ReportHybridSolutions via METRIC_STYLES.
// Long-form labels here; short-form ('Vaik.', 'Tot.', etc.) used in pill chips.
const EVAL_ICONS = { impact: Zap, feasibility: Target, innovation: Lightbulb, evidence: BookOpen };
const EVAL_STYLES = Object.fromEntries(
  Object.entries(METRIC_STYLES).map(([k, v]) => [k, { ...v, icon: EVAL_ICONS[k] }])
);

const EvalCard = styled.div`
  background: ${p => p.$bg};
  border: 1px solid ${p => p.$border};
  border-radius: 0;
  padding: 14px;
`;

const EvalCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

const EvalLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${p => p.$color};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const EvalScore = styled.span`
  margin-left: auto;
  font-size: 22px;
  font-weight: 800;
  color: ${p => p.$color};
`;

const EvalReasoning = styled.p`
  font-size: 13px;
  line-height: 1.5;
  color: #4b5563;
  margin: 0;
`;

const EvalCurrentState = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;
  margin: 8px 0 0;
  padding-top: 8px;
  border-top: 1px solid ${p => p.$border};
  font-style: italic;

  &::before {
    content: 'Nykytila: ';
    font-weight: 600;
    font-style: normal;
  }
`;

// ─── Insight / Conclusion ──────────────────────────────────────────────────

const InsightBlock = styled.div`
  background: ${p => p.$bg || '#fafafa'};
  border-radius: 2px;
  padding: 14px;
  margin-bottom: 12px;
`;

const InsightText = styled.p`
  font-size: 13px;
  line-height: 1.6;
  color: ${p => p.$color || '#4b5563'};
  margin: 0;
  font-style: italic;
`;

const ConclusionBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background: ${p => p.$bg || '#f5f3ff'};
  border: 1px solid ${p => p.$border || '#ddd6fe'};
  border-radius: 0;
  padding: 16px 20px;
  margin-top: 20px;
`;

const ConclusionScore = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #7c3aed;
  white-space: nowrap;
  line-height: 1;

  span {
    font-size: 14px;
    font-weight: 600;
    color: #a78bfa;
  }
`;

const ConclusionText = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${p => p.$color || '#000'};
  font-weight: 500;
`;

const RefList = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 8px;
  span { margin-right: 12px; }
`;

// ─── Sub-components ────────────────────────────────────────────────────────

/** Tag-based evaluation (version 2) from solution analysis pipeline. */
export function isTagBasedEvaluation(rawEv) {
  const ev = rawEv && typeof rawEv === 'object' ? rawEv : null;
  if (!ev) return false;
  if (ev.version === 2 && Array.isArray(ev.tags)) return true;
  const tags = Array.isArray(ev.tags) ? ev.tags : [];
  return tags.some((t) => t?.id && isValidEvaluationTagId(t.id));
}

/** Tags from full analysis or from enrich-step metadata (for list previews). */
export function getSolutionTagPreview(solution) {
  const ev = solution?.ai_analysis?.evaluation;
  if (isTagBasedEvaluation(ev)) {
    return { tags: ev.tags, contradicts: ev.contradicts_research || null };
  }
  const m = solution?.metadata?.enrichTags;
  if (Array.isArray(m) && m.length) {
    return { tags: m, contradicts: solution.metadata?.enrichContradictsResearch || null };
  }
  return { tags: [], contradicts: null };
}

export function TagEvaluationSection({ evaluation: rawEvaluation }) {
  const evaluation = sanitizeAiObject(rawEvaluation);
  if (!isTagBasedEvaluation(evaluation)) return null;

  return (
    <>
      <InsightLabel style={{ marginBottom: 8, marginTop: 16 }}>
        <Target size={13} /> Arviointi
      </InsightLabel>
      <EvaluationTagChips
        tags={evaluation.tags}
        contradictsResearch={evaluation.contradicts_research}
        researchStance={evaluation.research_stance}
        variant="light"
      />
    </>
  );
}

export function EvaluationSection({ evaluation: rawEvaluation, solutionId = null }) {
  const evaluation = sanitizeAiObject(rawEvaluation);
  if (!evaluation?.impact?.score) return null;

  return (
    <>
      <InsightLabel style={{ marginBottom: 8, marginTop: 16 }}>
        <Target size={13} /> Arviointi
      </InsightLabel>
      <EvalGrid>
        {Object.entries(EVAL_STYLES).map(([key, style]) => {
          const data = evaluation[key];
          if (!data?.score) return null;
          const Icon = style.icon;
          return (
            <EvalCard key={key} $bg={style.bg} $border={style.border}>
              <EvalCardHeader>
                <Icon size={14} color={style.color} />
                <EvalLabel $color={style.color}>{style.label}</EvalLabel>
                <EvalScore $color={style.color}>{data.score}/5</EvalScore>
              </EvalCardHeader>
              <EvalReasoning>
                {solutionId && data.reasoning
                  ? (
                    <EditableText
                      fieldKey={`solution.${solutionId}.evaluation.${key}.reasoning`}
                      fallback={data.reasoning}
                      label={`${style.label} — perustelu`}
                    />
                  )
                  : data.reasoning}
              </EvalReasoning>
              {key === 'innovation' && data.currentState && (
                <EvalCurrentState $border={style.border}>
                  {solutionId
                    ? (
                      <EditableText
                        fieldKey={`solution.${solutionId}.evaluation.${key}.currentState`}
                        fallback={data.currentState}
                        label={`${style.label} — nykytila`}
                      />
                    )
                    : data.currentState}
                </EvalCurrentState>
              )}
            </EvalCard>
          );
        })}
      </EvalGrid>
    </>
  );
}

// ─── Main component ────────────────────────────────────────────────────────

export function getCompositeScore(analysis) {
  const ev = analysis?.evaluation;
  if (isTagBasedEvaluation(ev)) return null;
  if (!ev?.impact?.score || !ev?.feasibility?.score || !ev?.innovation?.score) return null;
  const evidenceScore = ev.evidence?.score || 0;
  const hasEvidence = evidenceScore > 0;
  const composite = hasEvidence
    ? (ev.impact.score * 0.3) + (ev.feasibility.score * 0.25) + (ev.innovation.score * 0.2) + (evidenceScore * 0.25)
    : (ev.impact.score * 0.4) + (ev.feasibility.score * 0.3) + (ev.innovation.score * 0.3);
  return Math.round(composite * 10) / 10;
}

/**
 * Heuristic 1–5 score for tag-based evaluations. Used only when the admin
 * has opted in via instance.settings.showSolutionScore — the canonical
 * representation is still the tag chips. Formula:
 *   base 2.5 + 0.6 per strength tag − 1.0 if contradicts_research.
 * Strength tags: ambitious, feasible, innovative, creative, research_backed,
 *                systemic, tested_elsewhere, quick_to_implement.
 * Clamped to [0, 5], rounded to 1 decimal.
 */
const STRENGTH_TAGS = new Set([
  'ambitious', 'feasible', 'innovative', 'creative',
  'research_backed', 'systemic', 'tested_elsewhere', 'quick_to_implement',
]);
export function getTagBasedScore(analysis) {
  const ev = analysis?.evaluation;
  if (!isTagBasedEvaluation(ev) || !Array.isArray(ev.tags) || ev.tags.length === 0) return null;
  const strengthCount = ev.tags.filter(t => STRENGTH_TAGS.has(t.id)).length;
  const contradicts = !!ev.contradicts_research?.reasoning;
  const raw = 2.5 + (strengthCount * 0.6) - (contradicts ? 1.0 : 0);
  const clamped = Math.max(0, Math.min(5, raw));
  return Math.round(clamped * 10) / 10;
}

/** Unified accessor: legacy composite OR tag-based heuristic. Null if neither applies. */
export function getSolutionScore(analysis) {
  const legacy = getCompositeScore(analysis);
  if (legacy != null) return legacy;
  return getTagBasedScore(analysis);
}

export function SolutionConclusion({ analysis: rawAnalysis, solutionId = null }) {
  const analysis = sanitizeAiObject(rawAnalysis);
  const ev = analysis?.evaluation;

  if (isTagBasedEvaluation(ev)) {
    if (!analysis?.overallAssessment) return null;
    return (
      <ConclusionBlock style={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <ConclusionText>
          {solutionId
            ? <EditableText fieldKey={`solution.${solutionId}.overallAssessment`} fallback={analysis.overallAssessment} />
            : analysis.overallAssessment}
        </ConclusionText>
      </ConclusionBlock>
    );
  }

  const score = getCompositeScore(analysis);
  if (score === null) return null;
  return (
    <ConclusionBlock>
      <ConclusionScore>{score} <span>/ 5</span></ConclusionScore>
      {analysis?.overallAssessment && (
        <ConclusionText>
          {solutionId
            ? <EditableText fieldKey={`solution.${solutionId}.overallAssessment`} fallback={analysis.overallAssessment} />
            : analysis.overallAssessment}
        </ConclusionText>
      )}
    </ConclusionBlock>
  );
}

export default function SolutionAnalysis({ analysis: rawAnalysis, hideConclusion = false, hideLenses = true, solutionId = null }) {
  const { t } = useTranslation();
  const analysis = sanitizeAiObject(rawAnalysis);
  if (!analysis?.swot) return null;
  const { swot, lensImpact, evaluation, researchSupport, implementationOutlook, overallAssessment, references } = analysis;

  // hideLenses defaults to true per käsikirjoitus 2026-05 ("Ratkaisuissa kohtaa
  // LINSSIT en ymmärrä ... Ehdotan, että poistetaan."). Direct SolutionPage view
  // can opt-in via hideLenses={false} if needed for deep-dive.
  const lensEntries = (hideLenses || !lensImpact) ? [] : Object.entries(lensImpact);

  return (
    <AnalysisSection>
      <AnalysisHeader>
        <AnalysisTag>AI</AnalysisTag>
        <AnalysisTitle>{t('solutionAnalysis.title')}</AnalysisTitle>
      </AnalysisHeader>

      {/* 1. SWOT */}
      <SwotGrid>
        {Object.entries(SWOT_STYLES).map(([key, style]) => {
          const items = swot[key];
          if (!items?.length) return null;
          const Icon = style.icon;
          return (
            <SwotCard key={key} $bg={style.bg} $border={style.border}>
              <SwotCardTitle $color={style.title}>
                <Icon size={14} /> {t(style.labelKey)}
              </SwotCardTitle>
              <SwotList>
                {items.map((item, i) => (
                  <li key={i}>
                    {solutionId
                      ? <EditableText
                          fieldKey={`solution.${solutionId}.swot.${key}.${i}`}
                          fallback={item}
                          label={`${t(style.labelKey)} #${i + 1}`}
                        />
                      : item}
                  </li>
                ))}
              </SwotList>
            </SwotCard>
          );
        })}
      </SwotGrid>

      <AnalysisDivider />

      {/* 2. Lenses */}
      {lensEntries.length > 0 && (
        <LensFocusWrap>
          <InsightLabel style={{ marginBottom: 8 }}><Target size={13} /> {t('report.analysis.lensesTitle')}</InsightLabel>
          <LensFocusRow>
            {lensEntries
              .sort((a, b) => Math.abs(b[1].score) - Math.abs(a[1].score))
              .map(([lens, data]) => {
                const intensity = Math.abs(data.score);
                const level = FOCUS_LEVEL[Math.min(intensity, 2)];
                return (
                  <LensChip key={lens} $bg={level.bg} $border={level.border} $color={level.color} $intensity={intensity}>
                    {data.score !== 0 && (
                      <LensSign $positive={data.score > 0} $negative={data.score < 0}>
                        {data.score > 0 ? '+' : '−'}
                      </LensSign>
                    )}
                    {lens}
                  </LensChip>
                );
              })}
          </LensFocusRow>
          <LensReasonList>
            {lensEntries
              .filter(([, data]) => data.reasoning && Math.abs(data.score) >= 1)
              .sort((a, b) => Math.abs(b[1].score) - Math.abs(a[1].score))
              .map(([lens, data]) => (
                <LensReasonItem key={lens} $intensity={Math.abs(data.score)}>
                  <LensReasonName>{lens}:</LensReasonName>{' '}
                  {solutionId
                    ? <EditableText fieldKey={`solution.${solutionId}.lensImpact.${lens}.reasoning`} fallback={data.reasoning} label={`${lens} — linssin perustelu`} />
                    : data.reasoning}
                </LensReasonItem>
              ))}
          </LensReasonList>
        </LensFocusWrap>
      )}

      <AnalysisDivider />

      {/* 3. Evaluation */}
      {isTagBasedEvaluation(evaluation)
        ? <TagEvaluationSection evaluation={evaluation} />
        : <EvaluationSection evaluation={evaluation} solutionId={solutionId} />}

      {/* 4. Implementation outlook */}
      {implementationOutlook && (
        <InsightBlock>
          <InsightLabel><TrendingUp size={13} /> {t('report.analysis.implementationOutlook')}</InsightLabel>
          <InsightText>
            {solutionId
              ? <EditableText fieldKey={`solution.${solutionId}.implementationOutlook`} fallback={implementationOutlook} />
              : implementationOutlook}
          </InsightText>
        </InsightBlock>
      )}

      {/* 5. References — grouped by knowledge tier (RAG vs web) */}
      {references?.length > 0 && (() => {
        const grouped = {};
        references.forEach(r => {
          const tier = r.tier || 'rag';
          (grouped[tier] ||= []).push(r);
        });
        const tiers = TIER_ORDER.filter(t => grouped[t]?.length);
        // Single-tier RAG (legacy): render the flat list as before
        if (tiers.length === 1 && tiers[0] === 'rag') {
          return (
            <RefList>
              {grouped.rag.map((r, i) => (
                <span key={r.id}>
                  [{r.id}]{' '}
                  {solutionId
                    ? <EditableText fieldKey={`solution.${solutionId}.references.${i}.title`} fallback={r.title} multiline={false} />
                    : r.title}
                  {r.author ? ` — ${r.author}` : ''}
                  {r.year ? ` (${r.year})` : ''}
                </span>
              ))}
            </RefList>
          );
        }
        // Mixed tiers: header per section, web sources get URL links
        return (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tiers.map(tierId => {
              const meta = getTier(tierId);
              const items = grouped[tierId];
              return (
                <div key={tierId}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <SourceTag tier={tierId} count={items.length} />
                    {meta.disclaimerKey && (
                      <span style={{ fontSize: 11, fontStyle: 'italic', color: '#6b7280' }}>{t(meta.disclaimerKey)}</span>
                    )}
                  </div>
                  <RefList>
                    {items.map((r) => {
                      const i = references.indexOf(r);
                      return (
                      <span key={r.id}>
                        [{r.id}]{' '}
                        {solutionId
                          ? <EditableText fieldKey={`solution.${solutionId}.references.${i}.title`} fallback={r.title} multiline={false} />
                          : r.title}
                        {r.author ? ` — ${r.author}` : ''}
                        {r.year ? ` (${r.year})` : ''}
                        {r.url && (
                          <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 4, color: meta.color, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}>
                            <ExternalLink size={10} />
                          </a>
                        )}
                      </span>
                      );
                    })}
                  </RefList>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* 6. Conclusion */}
      {!hideConclusion && (
        <SolutionConclusion analysis={analysis} solutionId={solutionId} />
      )}
    </AnalysisSection>
  );
}
